import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    getLeadAccountById, 
    createLeadAccount, 
    updateLeadAccount, 
    getLeadSources, 
    getUsersWithLeadRole 
} from '../api';
import { logError } from '../api';
import FormContainer from './Global/FormContainer';

const LeadAccountForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        zipCode: '',
        leadSourceId: '',
        assignedLeadId: ''
    });

    const [leadSources, setLeadSources] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadDropdownData();
        if (isEdit) {
            loadLeadAccount();
        }
    }, [id, isEdit]);

    const loadDropdownData = async () => {
        try {
            const [leadSourcesData, leadsData] = await Promise.all([
                getLeadSources(),
                getUsersWithLeadRole(9) // 9 es el roleId para leads
            ]);
            setLeadSources(leadSourcesData || []);
            setLeads((leadsData || []).filter(user => !user.disabled));
        } catch (error) {
            logError(error);
            console.error('Error loading dropdown data:', error);
        }
    };

    const loadLeadAccount = async () => {
        try {
            const leadAccount = await getLeadAccountById(id);
            setFormData({
                name: leadAccount.name || '',
                email: leadAccount.email || '',
                zipCode: leadAccount.zipCode || '',
                leadSourceId: leadAccount.leadSource?.valueId || '',
                assignedLeadId: leadAccount.assignedLeadId || ''
            });
        } catch (error) {
            logError(error);
            setError('Error al cargar la cuenta de lead');
            console.error('Error loading lead account:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value === '' ? '' : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const dataToSubmit = {
                ...formData,
                leadSourceId: parseInt(formData.leadSourceId),
                assignedLeadId: formData.assignedLeadId ? parseInt(formData.assignedLeadId) : null
            };

            if (isEdit) {
                await updateLeadAccount(id, { id: parseInt(id), ...dataToSubmit });
            } else {
                await createLeadAccount(dataToSubmit);
            }
            
            navigate('/lead-accounts');
        } catch (error) {
            logError(error);
            setError(error.message || 'Error al guardar la cuenta de lead');
            console.error('Error saving lead account:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className="my-4">
            <FormContainer
                title={isEdit ? 'Editar Cuenta de Lead' : 'Nueva Cuenta de Lead'}
            >
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                maxLength={255}
                                data-testid="lead-account-name-input"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                maxLength={255}
                                data-testid="lead-account-email-input"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Código Postal</Form.Label>
                            <Form.Control
                                type="text"
                                name="zipCode"
                                value={formData.zipCode}
                                onChange={handleChange}
                                maxLength={20}
                                data-testid="lead-account-zipcode-input"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Fuente de Lead *</Form.Label>
                            <Form.Select
                                name="leadSourceId"
                                value={formData.leadSourceId}
                                onChange={handleChange}
                                required
                                data-testid="lead-account-source-select"
                            >
                                <option value="">Seleccionar fuente</option>
                                {leadSources.map(source => (
                                    <option key={source.valueId} value={source.valueId}>
                                        {source.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Lead Asignado</Form.Label>
                            <Form.Select
                                name="assignedLeadId"
                                value={formData.assignedLeadId}
                                onChange={handleChange}
                                data-testid="lead-account-assigned-lead-select"
                            >
                                <option value="">Seleccionar lead</option>
                                {leads.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.username}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row className="mt-4">
                    <Col className="d-flex justify-content-end gap-2">
                        <Button 
                            variant="secondary" 
                            onClick={() => navigate('/lead-accounts')}
                            disabled={loading}
                            data-testid="lead-account-cancel-button"
                        >
                            Cancelar
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit"
                            disabled={loading}
                            data-testid="lead-account-submit-button"
                        >
                            {loading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear')}
                        </Button>
                    </Col>
                </Row>
                </Form>
            </FormContainer>
        </Container>
    );
};

export default LeadAccountForm;